import { useQuery, type UseQueryResult } from 'react-query'
import { gql } from 'graphql-request'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { gqlQuery } from '../services/requests.service'
import type { FeatureKey } from '@hapana/shared/schemas/entitlements/feature-key.schema'
import type { ThemeKey } from '@hapana/shared/schemas/entitlements/theme-key.schema'
import type { PlanTier } from '@hapana/shared/schemas/entitlements/plan-tier.schema'
import type { SiteEntitlementsSchema } from '@hapana/shared/schemas/entitlements/site-entitlements.schema'
import type { FeatureCheckResult } from '@hapana/shared/schemas/entitlements/entitlement-result.schema'
import {
  FEATURE_LABEL_KEYS,
  PLAN_LABEL_KEYS,
  THEME_LABEL_KEYS,
  THEME_ELIGIBILITY,
  getRequiredPlanForFeature,
  getThemeForFeature,
  meetsMinimumPlan,
} from '@hapana/shared/constants/entitlements.constants'

export const SITE_ENTITLEMENTS_QUERY_KEY = 'SITE_ENTITLEMENTS'

/**
 * What a location is entitled to (OC-320).
 *
 * `entitledFeatures` is the list the UI gates on. While product tiering is switched off for the
 * brand — its state for every brand in production today — the backend returns EVERY feature, so
 * anything built on this behaves exactly as it did before tiering existed. Gating can only ever
 * subtract, and only for a brand someone has deliberately switched on.
 *
 * `eligibleThemes` is what the plan COULD buy; `activeThemes` is what it holds — now derived from
 * the brand's plan tier by the entitlement engine (OC-320), so theme features are entitled by tier.
 */
const siteEntitlementsQuery = gql`
  query SiteEntitlements($siteId: ID!) {
    siteEntitlements(siteId: $siteId) {
      effectivePlan
      entitledFeatures
      eligibleThemes
      activeThemes
    }
  }
`

interface SiteEntitlementsResponse {
  siteEntitlements: SiteEntitlementsSchema
}

const getSiteEntitlements = gqlQuery<SiteEntitlementsResponse>({
  key: SITE_ENTITLEMENTS_QUERY_KEY,
  query: siteEntitlementsQuery,
  options: { isV1: false },
})

/**
 * Read a location's entitlements.
 *
 * Cached for five minutes: a plan changes rarely, and every gated component in the app will call
 * this, so refetching per mount would put the tier read on the critical path of most screens.
 */
export const useSiteEntitlements = (siteId: string | undefined, enabled = true) =>
  // `enabled` is the product-tiering flag gate, threaded from the provider (which reads the same flag
  // the backend gates on): flag off ⇒ skip the fetch ⇒ no data ⇒ hasFeature fails open ⇒ nothing
  // gated, mirroring the backend's `if (!shouldGateFeatures) …`. The hook itself stays flag-agnostic.
  //
  // Three generics, so nothing downstream needs a cast: what the query returns, the error type,
  // and what `select` narrows it to — which is what `data` ends up being at the call site.
  //
  // Variables go under `meta`: gqlQuery's function destructures { meta, queryKey, pageParam } and
  // spreads those into the GraphQL variables. Passing a bare object sets none of them, so the
  // request goes out with variables {} and the server rejects the required argument.
  useQuery<SiteEntitlementsResponse, Error, SiteEntitlementsSchema | null>(
    [SITE_ENTITLEMENTS_QUERY_KEY, { siteId }],
    () => getSiteEntitlements.query({ meta: { siteId } }),
    {
      enabled: !!siteId && enabled,
      select: (data) => data?.siteEntitlements ?? null,
      staleTime: 5 * 60_000,
      // No keepPreviousData: switching to a not-yet-loaded location must not show the previous
      // location's tier (with isLoading:false) and paint its locks against the new one — a stale
      // flash the provider's loading gate can't catch. Dropping it makes a key change read as
      // loading, so the provider waits and paints once at the new location's tier.
    }
  )

export const CORPORATE_ENTITLEMENTS_QUERY_KEY = 'CORPORATE_ENTITLEMENTS'

const corporateEntitlementsQuery = gql`
  query CorporateEntitlements {
    corporateEntitlements {
      effectivePlan
      entitledFeatures
      eligibleThemes
      activeThemes
    }
  }
`

interface CorporateEntitlementsResponse {
  corporateEntitlements: SiteEntitlementsSchema
}

const getCorporateEntitlements = gqlQuery<CorporateEntitlementsResponse>({
  key: CORPORATE_ENTITLEMENTS_QUERY_KEY,
  query: corporateEntitlementsQuery,
  options: { isV1: false },
})

/**
 * Read the BRAND's entitlements — its plan tier and the features that tier grants, with no location
 * override. This is what corporate-level surfaces (`/c/*`) gate on: they have no location in
 * context, so the site-scoped read would have no siteId and fail open. The server resolves the brand
 * from the request's tenant, so no id is sent.
 *
 * `corporateId` scopes the CACHE KEY only — it is never sent to the server. Core resolves the brand
 * from its own subdomain and passes nothing, so the key stays `[CORPORATE_ENTITLEMENTS_QUERY_KEY]`
 * exactly as before. Admin views many brands in one session over a per-brand `X-Tenant` header, so
 * it passes the target brand's id to keep each brand's five-minute cache entry distinct — otherwise
 * every brand would share one entry and a switch would read the previously-opened brand's tier.
 *
 * Cached for five minutes, like the site read: a plan changes rarely and most gated screens call it.
 */
export const useCorporateEntitlementsQuery = (corporateId?: string, enabled = true) =>
  // `enabled` = the product-tiering flag gate the caller threads in. Core passes its flag so the fetch
  // is skipped (fail-open) while tiering is off; admin passes nothing (defaults to always-on) because
  // it cannot evaluate the target brand's flag client-side and the server gates per-brand instead.
  // `corporateId` first, matching the site-scoped hooks' (id, enabled) order.
  useQuery<CorporateEntitlementsResponse, Error, SiteEntitlementsSchema | null>(
    corporateId
      ? [CORPORATE_ENTITLEMENTS_QUERY_KEY, { corporateId }]
      : [CORPORATE_ENTITLEMENTS_QUERY_KEY],
    () => getCorporateEntitlements.query({ meta: {} }),
    {
      enabled,
      select: (data) => data?.corporateEntitlements ?? null,
      staleTime: 5 * 60_000,
      // No keepPreviousData — see useSiteEntitlements: a brand switch must read as loading, not
      // paint the previously-opened brand's tier against the next brand's surfaces.
    }
  )

export interface FeatureAccess {
  /** True when the feature is available. Defaults to TRUE while loading — see below. */
  hasFeature: boolean
  isLoading: boolean
  effectivePlan: PlanTier | null
  eligibleThemes: ThemeKey[]
}

/**
 * Whether a location may use one feature — the hook screens call to gate themselves.
 *
 * ⚠️ FAILS OPEN. `hasFeature` is true while the query is loading, and true if it errors. That is
 * deliberate: a gated screen that briefly renders for someone entitled to it is a cosmetic flicker,
 * whereas hiding a paid feature because a network request was slow is indistinguishable from
 * breaking the product. The server is the real boundary — RequireFeature enforces on the backend —
 * so this is UX only and does not need to be conservative.
 */
export const useFeatureAccess = (
  siteId: string | undefined,
  feature: FeatureKey
): FeatureAccess => {
  const { data, isLoading, isError } = useSiteEntitlements(siteId)

  if (isLoading || isError || !data) {
    return { hasFeature: true, isLoading, effectivePlan: null, eligibleThemes: [] }
  }

  return {
    hasFeature: data.entitledFeatures.includes(feature),
    isLoading: false,
    effectivePlan: data.effectivePlan,
    eligibleThemes: data.eligibleThemes,
  }
}

/**
 * Everything the feature-gate components need from one location's entitlements.
 *
 * Read once high in a screen and pass down, rather than having every gated leaf mount its own
 * query — which is why the gate components take this object as a prop instead of a siteId.
 */
export interface UseEntitlementsReturn {
  entitlements: SiteEntitlementsSchema | null
  isLoading: boolean
  error: unknown
  hasFeature: (feature: FeatureKey) => boolean
  checkFeature: (feature: FeatureKey) => FeatureCheckResult
  isThemeActive: (theme: ThemeKey) => boolean
  refetch: () => void
}

/**
 * Build the gate-facing view from a raw entitlements query result — site-scoped or brand-scoped.
 * Both `useEntitlements` and `useBrandEntitlements` share this so the two scopes can never drift.
 *
 * ⚠️ FAILS OPEN: while the query is loading or after it errors every feature reads as entitled.
 * Client gating is UX; `RequireFeature` on the backend is the real boundary, so a slow or failed
 * request must never hide something a customer pays for.
 */
const buildEntitlementsReturn = (
  t: TFunction,
  query: UseQueryResult<SiteEntitlementsSchema | null, Error>
): UseEntitlementsReturn => {
  const { data, isLoading, isError, error, refetch } = query

  const entitlements = data ?? null
  // No data yet (or the read failed) means "do not gate", not "gate everything".
  const isUnknown = isLoading || isError || !entitlements

  const hasFeature = (feature: FeatureKey): boolean =>
    isUnknown || entitlements.entitledFeatures.includes(feature)

  const isThemeActive = (theme: ThemeKey): boolean =>
    !isUnknown && entitlements.activeThemes.includes(theme)

  /**
   * Why a feature is unavailable and the cheapest route to it.
   *
   * Mirrors `EntitlementService.checkFeature` on the backend so the message a user reads matches
   * the reason the server would give: plan features name the plan that grants them, theme features
   * name the theme — and name the plan too when the location's plan cannot even buy that theme.
   */
  const checkFeature = (feature: FeatureKey): FeatureCheckResult => {
    if (hasFeature(feature)) {
      return { entitled: true }
    }

    const featureLabel = t(FEATURE_LABEL_KEYS[feature])

    const requiredPlan = getRequiredPlanForFeature(feature)
    if (requiredPlan) {
      return {
        entitled: false,
        upgradeInfo: {
          type: 'plan',
          requiredPlan,
          message: t('entitlements.upgrade.plan', {
            plan: t(PLAN_LABEL_KEYS[requiredPlan]),
            feature: featureLabel,
          }),
        },
      }
    }

    const requiredTheme = getThemeForFeature(feature)
    if (requiredTheme) {
      const themeLabel = t(THEME_LABEL_KEYS[requiredTheme])
      const themeMinimumPlan = THEME_ELIGIBILITY[requiredTheme]
      const effectivePlan = entitlements?.effectivePlan

      if (effectivePlan && !meetsMinimumPlan(effectivePlan, themeMinimumPlan)) {
        return {
          entitled: false,
          upgradeInfo: {
            type: 'plan',
            requiredPlan: themeMinimumPlan,
            requiredTheme,
            message: t('entitlements.upgrade.planAndTheme', {
              plan: t(PLAN_LABEL_KEYS[themeMinimumPlan]),
              theme: themeLabel,
              feature: featureLabel,
            }),
          },
        }
      }

      return {
        entitled: false,
        upgradeInfo: {
          type: 'theme',
          requiredTheme,
          message: t('entitlements.upgrade.theme', {
            theme: themeLabel,
            feature: featureLabel,
          }),
        },
      }
    }

    return { entitled: false }
  }

  return { entitlements, isLoading, error, hasFeature, checkFeature, isThemeActive, refetch }
}

/**
 * Gate-facing view of a LOCATION's entitlements (folds in the site's tier override). `enabled` is
 * the product-tiering flag gate the provider passes in — off ⇒ no fetch ⇒ fail-open ⇒ nothing gated.
 */
export const useEntitlements = (
  siteId: string | undefined,
  enabled = true
): UseEntitlementsReturn => {
  const { t } = useTranslation('common')
  return buildEntitlementsReturn(t, useSiteEntitlements(siteId, enabled))
}

/**
 * Gate-facing view of the BRAND's entitlements — no location in context, no override. This is what
 * corporate-level surfaces (`/c/*`) gate on. `enabled` is the product-tiering flag gate from the
 * provider — off ⇒ no fetch ⇒ fail-open ⇒ nothing gated. `corporateId` scopes the cache key when the
 * caller (admin) targets brands other than its own; Core omits it — see `useCorporateEntitlementsQuery`.
 */
export const useBrandEntitlements = (
  corporateId?: string,
  enabled = true
): UseEntitlementsReturn => {
  const { t } = useTranslation('common')
  return buildEntitlementsReturn(t, useCorporateEntitlementsQuery(corporateId, enabled))
}
